import { useEffect } from "react";

interface ProductMeta {
  price?: number;
  currency?: string;
  availability?: string;
  brand?: string;
  category?: string;
}

interface PageMeta {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  prevPage?: string;
  nextPage?: string;
  robots?: string;
  ogType?: string;
  productMeta?: ProductMeta;
  articleMeta?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
}

export function usePageMeta({ title, description, canonical, ogImage, prevPage, nextPage, robots, ogType, productMeta, articleMeta }: PageMeta) {
  useEffect(() => {
    const createdTags: HTMLElement[] = [];

    const ensureMeta = (property: string, isName: boolean, value: string) => {
      const attr = isName ? "name" : "property";
      let el = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, property);
        document.head.appendChild(el);
        createdTags.push(el);
      }
      el.content = value;
    };

    document.title = title;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", description);

    // Canonical URL
    const siteUrl = "https://www.atacadooptico.com.br";
    const canonicalUrl = canonical || (siteUrl + window.location.pathname);
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.rel = "canonical";
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = canonicalUrl;

    // og:url
    let ogUrl = document.querySelector('meta[property="og:url"]') as HTMLMetaElement | null;
    if (!ogUrl) {
      ogUrl = document.createElement("meta");
      ogUrl.setAttribute("property", "og:url");
      document.head.appendChild(ogUrl);
    }
    ogUrl.content = canonicalUrl;

    // og:image override
    if (ogImage) {
      setMeta('meta[property="og:image"]', "content", ogImage);
      setMeta('meta[name="twitter:image"]', "content", ogImage);
    }

    // og:type
    ensureMeta("og:type", false, ogType || "website");

    // robots (default indexável; evita herdar noindex de páginas anteriores no SPA)
    ensureMeta("robots", true, robots || "index, follow");


    // twitter:card
    ensureMeta("twitter:card", true, "summary_large_image");

    // og:article meta tags (Facebook/LinkedIn compartilhamento)
    if (articleMeta) {
      if (articleMeta.publishedTime) ensureMeta("article:published_time", false, articleMeta.publishedTime);
      if (articleMeta.modifiedTime) ensureMeta("article:modified_time", false, articleMeta.modifiedTime);
      if (articleMeta.author) ensureMeta("article:author", false, articleMeta.author);
      if (articleMeta.section) ensureMeta("article:section", false, articleMeta.section);
      if (articleMeta.tags) {
        articleMeta.tags.forEach(tag => {
          const el = document.createElement("meta");
          el.setAttribute("property", "article:tag");
          el.content = tag;
          document.head.appendChild(el);
          createdTags.push(el);
        });
      }
    }

    // product meta tags
    if (productMeta) {
      if (productMeta.price !== undefined) {
        ensureMeta("product:price:amount", false, String(productMeta.price));
      }
      if (productMeta.currency) {
        ensureMeta("product:price:currency", false, productMeta.currency);
      }
      if (productMeta.availability) {
        ensureMeta("product:availability", false, productMeta.availability);
      }
      if (productMeta.brand) {
        ensureMeta("product:brand", false, productMeta.brand);
      }
      if (productMeta.category) {
        ensureMeta("product:category", false, productMeta.category);
      }
    }

    // rel="prev" / rel="next" for paginated pages
    const setPaginationLink = (rel: string, href: string | undefined) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (href) {
        if (!el) {
          el = document.createElement("link");
          el.rel = rel;
          document.head.appendChild(el);
        }
        el.href = href;
      } else {
        el?.remove();
      }
    };
    setPaginationLink("prev", prevPage);
    setPaginationLink("next", nextPage);

    return () => {
      document.querySelector('link[rel="prev"]')?.remove();
      document.querySelector('link[rel="next"]')?.remove();
      createdTags.forEach((el) => el.remove());
    };
  }, [title, description, canonical, ogImage, prevPage, nextPage, robots, ogType, productMeta, articleMeta]);
}
